// src/context/CartContext.js
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { userService } from '../services/userService';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [giftPacking, setGiftPacking] = useState(false);
  const { user, isAuthenticated, loading } = useAuth();
  const isGuest = user?.role === 'guest';

  const isSyncingRef = useRef(false);
  const hasSyncedRef = useRef(false);

  // Helper to extract uniform ID
  const getItemId = (item) => item?._id || item?.id;

  // 1. Load data from localStorage for guest/unauthenticated users
  useEffect(() => {
    if (loading) return;
    
    if (!isAuthenticated && !isInitialLoadComplete) {
      const savedCart = localStorage.getItem('cart');
      const savedWishlist = localStorage.getItem('wishlist');
      const savedGiftPacking = localStorage.getItem('giftPacking');
      
      if (savedCart) {
        try { setCart(JSON.parse(savedCart)); } catch (e) { console.error(e); }
      }
      if (savedWishlist) {
        try { setWishlist(JSON.parse(savedWishlist)); } catch (e) { console.error(e); }
      }
      if (savedGiftPacking) {
        try { setGiftPacking(JSON.parse(savedGiftPacking)); } catch (e) { console.error(e); }
      }
      setIsInitialLoadComplete(true);
    }
  }, [loading, isAuthenticated, isInitialLoadComplete]);

  // 2. Sync cart/wishlist from database on login
  const syncFromDatabase = useCallback(async () => {
    if (!isAuthenticated || isSyncingRef.current || hasSyncedRef.current) {
      return;
    }
    
    isSyncingRef.current = true;
    console.log('Starting database sync...');
    
    try {
      // Fetch Wishlist
      try {
        const wishlistRes = await userService.getWishlist();
        if (wishlistRes?.success && wishlistRes?.data) {
          setWishlist(wishlistRes.data);
          localStorage.setItem('wishlist', JSON.stringify(wishlistRes.data));
        } else {
          setWishlist([]);
          localStorage.setItem('wishlist', JSON.stringify([]));
        }
      } catch (err) {
        console.error('Error fetching wishlist:', err);
      }

      // Fetch Cart
      try {
        const cartRes = await userService.getCart();
        if (cartRes?.success && cartRes?.data) {
          const dbCart = cartRes.data
            .map(item => ({
              ...item.product,
              quantity: item.quantity
            }))
            .filter(item => item && getItemId(item));
          
          setCart(dbCart);
          localStorage.setItem('cart', JSON.stringify(dbCart));
        } else {
          setCart([]);
          localStorage.setItem('cart', JSON.stringify([]));
        }
      } catch (err) {
        console.error('Error fetching cart:', err);
      }
      
      hasSyncedRef.current = true;
    } finally {
      isSyncingRef.current = false;
    }
  }, [isAuthenticated]);

  // Trigger Sync on Auth
  useEffect(() => {
    if (isAuthenticated && user) {
      syncFromDatabase();
    }
  }, [isAuthenticated, user, syncFromDatabase]);

  // LocalStorage Sync Effects
  useEffect(() => {
    if (isInitialLoadComplete || isAuthenticated) {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart, isInitialLoadComplete, isAuthenticated]);

  useEffect(() => {
    if (isInitialLoadComplete || isAuthenticated) {
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
    }
  }, [wishlist, isInitialLoadComplete, isAuthenticated]);

  useEffect(() => {
    localStorage.setItem('giftPacking', JSON.stringify(giftPacking));
  }, [giftPacking]);

  // Detect Logout & Account Switching
  useEffect(() => {
    if (!isAuthenticated && hasSyncedRef.current) {
      console.log('User logged out, clearing local cart...');
      setCart([]);
      setWishlist([]);
      localStorage.removeItem('cart');
      localStorage.removeItem('wishlist');
      localStorage.removeItem('currentUserId');
      hasSyncedRef.current = false;
      isSyncingRef.current = false;
    }

    if (isAuthenticated && user) {
      const savedUserId = localStorage.getItem('currentUserId');
      if (savedUserId && savedUserId !== user._id) {
        console.log('User switched account, resetting flags...');
        hasSyncedRef.current = false;
        isSyncingRef.current = false;
        setCart([]);
        setWishlist([]);
        syncFromDatabase();
      }
      localStorage.setItem('currentUserId', user._id);
    }
  }, [isAuthenticated, user, syncFromDatabase]);

  // Save Cart to DB Effect (Debounced)
  useEffect(() => {
    if (isAuthenticated && !isSyncingRef.current && hasSyncedRef.current) {
      const saveCartToDb = async () => {
        try {
          const dbCart = cart.map(item => ({
            product: getItemId(item),
            quantity: item.quantity
          }));
          await userService.saveCart(dbCart);
        } catch (err) {
          console.error('Error saving cart to database:', err);
        }
      };
      
      const timeoutId = setTimeout(saveCartToDb, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [cart, isAuthenticated]);

  // --- FIXED ADD TO CART FUNCTION ---
  const addToCart = (productToAdd, passedQuantity = null) => {
    if (isGuest || !productToAdd) return;

    const targetId = getItemId(productToAdd);
    // Explicitly determine quantity from passed argument OR inside product object OR default 1
    const quantityToAdd = passedQuantity !== null 
      ? passedQuantity 
      : (productToAdd.quantity || 1);

    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex((item) => getItemId(item) === targetId);

      if (existingIndex > -1) {
        // Product already in cart: Add quantities together
        return prevCart.map((item, idx) =>
          idx === existingIndex
            ? { ...item, quantity: item.quantity + quantityToAdd }
            : item
        );
      } else {
        // New Product: Add with exact chosen quantity
        return [...prevCart, { ...productToAdd, quantity: quantityToAdd }];
      }
    });
  };

  // --- REMOVE FROM CART ---
  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => getItemId(item) !== productId));
  };

  // --- UPDATE QUANTITY ---
  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart((prevCart) =>
        prevCart.map((item) =>
          getItemId(item) === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCart([]);
  };

  // --- TOGGLE WISHLIST ---
  const toggleWishlist = async (product) => {
    if (isGuest || !product) return;
    const targetId = getItemId(product);

    setWishlist((prevWishlist) => {
      const isInList = prevWishlist.some((item) => getItemId(item) === targetId);
      if (isInList) {
        return prevWishlist.filter((item) => getItemId(item) !== targetId);
      } else {
        return [...prevWishlist, product];
      }
    });

    if (isAuthenticated) {
      try {
        const isInList = wishlist.some((item) => getItemId(item) === targetId);
        if (isInList) {
          await userService.removeFromWishlist(targetId);
        } else {
          await userService.addToWishlist(targetId);
        }
      } catch (err) {
        console.error('Error updating wishlist on backend:', err);
      }
    }
  };

  const toggleGiftPacking = () => {
    setGiftPacking((prev) => !prev);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const value = {
    cart,
    wishlist,
    giftPacking,
    toggleGiftPacking,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleWishlist,
    cartTotal,
    cartCount,
    syncFromDatabase
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};